/**
 * File validation utilities for logo uploads
 */

export class FileValidationError extends Error {
  constructor(
    message: string,
    public code: 'SIZE_EXCEEDED' | 'INVALID_TYPE' | 'INVALID_MIME'
  ) {
    super(message);
    this.name = 'FileValidationError';
  }
}

/**
 * Validate file size
 */
export function validateFileSize(file: File, maxSizeMB: number): void {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  
  if (file.size > maxSizeBytes) {
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
    throw new FileValidationError(
      `File size ${fileSizeMB}MB exceeds maximum allowed size of ${maxSizeMB}MB`,
      'SIZE_EXCEEDED'
    );
  }
}

/**
 * Validate file type
 */
export function validateFileType(file: File, allowedTypes: string[]): void {
  if (!allowedTypes.includes(file.type)) {
    const allowedExtensions = allowedTypes
      .map((type) => {
        switch (type) {
          case 'image/png':
            return 'PNG';
          case 'image/jpeg':
          case 'image/jpg':
            return 'JPG';
          case 'image/svg+xml':
            return 'SVG';
          default:
            return type;
        }
      })
      .join(', ');

    throw new FileValidationError(
      `File type ${file.type} is not supported. Supported types: ${allowedExtensions}`,
      'INVALID_TYPE'
    );
  }
}

/**
 * Validate logo file (combines size and type validation)
 */
export function validateLogoFile(file: File): void {
  const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
  const maxSizeMB = 2;

  validateFileSize(file, maxSizeMB);
  validateFileType(file, allowedTypes);
}
